import { PDKToastInfoBadgeIcon } from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { VehicleListing } from '@/models/registry';
import dayjs from 'dayjs';
import { ClockFading, Images, Link } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import { VehicleListingTimeStatusBadge } from '../ui/vehicle-listing-time-status-badge';
import { AutopilotSelector } from './autopilot/autopilot-selector';
import { BatteryCapacityInput } from './battery-capacity/battery-capacity-input';
import { BatteryWarrantyMilesInput } from './battery-warranty-miles/battery-warranty-miles-input';
import { BatteryWarrantyMonthsInput } from './battery-warranty-months/battery-warranty-months-input';
import { VehicleChargingPlugSelector } from './charger/charger-type-selector';
import { EPARangeInput } from './epa-range/epa-range-input';
import { VehicleHistorySelector } from './history/vehicle-history-selector';
import { VehicleNumberOfKeysSelector } from './keys/number-of-keys-selector';
import { VehiclePreviousOwnersSelector } from './previous-owners/previous-owners-selector';
import { VehiclePurchaseStatusBadge } from './purchase-status/vehicle-purchase-status-badge';
import { VehicleSeatingCapacitySelector } from './seating-capacity/seating-capacity-selector';
import { VehicleStatusBadge } from './status/vehicle-status-badge';
import { VehicleStatusSelector } from './status/vehicle-status-selector';
import VehicleWholesaleStatusSelector from './wholesale/vehicle-wholesale-selector';
import VehicleWholesaleToggle from './wholesale/vehicle-wholesale-toggle';

const clipboardVIN = (vin: string | null | undefined) => {
  if (!vin) {
    toast({
      description: (
        <div className="flex flex-row gap-2 items-center">
          <PDKToastInfoBadgeIcon className="size-4" />
          <p>No VIN found</p>
        </div>
      ),
    });
    return;
  }

  navigator.clipboard.writeText(vin);
  toast({
    description: (
      <div className="flex flex-row gap-2 items-center">
        <PDKToastInfoBadgeIcon className="size-4" />
        <p>VIN copied to clipboard</p>
      </div>
    ),
  });
};

const NonMobileAside = observer(({ listing }: { listing: VehicleListing }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex flex-col dark:bg-dark">
      <header className="flex flex-row border-b">
        <div className="flex flex-row w-full h-10 shrink-0 items-center justify-between p-4 gap-2">
          <div className="text-xs text-gray-500">Properties</div>
          <div className="flex flex-row gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Images
                  className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                  onClick={() => {
                    window.open(
                      `https://garage.evercars.com/ever-certified-vehicles/${listing.id}`,
                      '_blank',
                    );
                  }}
                />
              </TooltipTrigger>
              <TooltipContent
                className="flex flex-row gap-2 items-center text-xs"
                side="bottom"
                align="end"
              >
                Go to Photos
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                  onClick={() => clipboardVIN(listing.vehicle?.vin)}
                />
              </TooltipTrigger>
              <TooltipContent
                className="flex flex-row gap-2 items-center text-xs"
                side="bottom"
                align="end"
              >
                Copy VIN
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </header>

      <div
        className="flex flex-col p-2 gap-4 h-[calc(100vh-40px)] overflow-auto ml-1"
        ref={scrollContainerRef}
      >
        <div className="flex flex-row gap-3 items-center">
          <div className="flex flex-col gap-2">
            <VehicleListingTimeStatusBadge
              dateString={listing.created_at}
              className="w-fit"
            />
            <div className="flex flex-row gap-2 items-center">
              <ClockFading className={cn('h-3.5 w-3.5 cursor-pointer', {})} />
              <p className="rounded text-xs  text-gray-600">
                {dayjs(listing.created_at).format('MMM D, YYYY h:mm A')}
              </p>
            </div>
          </div>
        </div>

        <div className="h-px bg-gray-200 w-full" />

        <div className="flex flex-row gap-3 items-center">
          {listing.purchase_status !== 'AVAILABLE' ? (
            <VehicleStatusBadge value={listing.status} className="px-2" />
          ) : (
            <VehicleStatusSelector
              id={listing.id}
              value={listing.status}
              className="px-2"
            />
          )}
        </div>

        <div className="flex flex-row gap-3 items-center">
          <VehiclePurchaseStatusBadge
            value={listing.purchase_status}
            className="px-2"
          />
        </div>

        <div className="flex flex-row gap-3 items-center">
          <VehicleWholesaleStatusSelector
            className="px-2"
            value={listing.transaction_type ?? 'RETAIL'}
          />
        </div>
        <div className="flex flex-row gap-3 items-center">
          <VehicleWholesaleToggle
            className="px-2"
            id={listing.id}
            value={listing.is_listed_wholesale ?? false}
          />
        </div>

        <div className="h-px bg-gray-200 w-full" />
        <div className="flex flex-row gap-3 items-center">
          <VehicleChargingPlugSelector
            id={listing.vehicle?.id ?? ''}
            value={listing.vehicle?.charging_plug || null}
            className="px-2"
          />
        </div>
        <div className="flex flex-row gap-3 items-center">
          <VehicleNumberOfKeysSelector
            id={listing.id}
            value={listing.num_of_keys}
            className="px-2"
          />
        </div>
        <div className="flex flex-row gap-3 items-center">
          <VehicleSeatingCapacitySelector
            vehicleId={listing.vehicle?.id ?? ''}
            value={listing.vehicle?.seating_capacity ?? 0}
            className="px-2"
          />
        </div>
        <div className="flex flex-row gap-3 items-center">
          <VehicleHistorySelector
            id={listing.id}
            value={listing.vehicle_history || null}
            className="px-2"
          />
        </div>
        <div className="h-px bg-gray-200 w-full" />
        <div className="flex flex-col">
          <div className="text-xs text-gray-500 px-2 py-1">
            Battery Capacity
          </div>
          <div className="flex flex-row gap-3 items-center">
            <BatteryCapacityInput
              id={listing.vehicle?.id ?? ''}
              value={listing.vehicle?.battery_capacity ?? 0}
              className="px-2"
            />
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-xs text-gray-500 px-2 py-1">EPA Range</div>
          <div className="flex flex-row gap-3 items-center">
            <EPARangeInput
              id={listing.vehicle?.id ?? ''}
              value={listing.vehicle?.epa_range ?? 0}
              className="px-2"
            />
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-xs text-gray-500 px-2 py-1">
            Remaining Battery Warranty Miles
          </div>
          <div className="flex flex-row gap-3 items-center">
            <BatteryWarrantyMilesInput
              id={listing.id}
              value={listing.remaining_battery_warranty_miles ?? 0}
              className="px-2"
            />
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-xs text-gray-500 px-2 py-1">
            Remaining Battery Warranty Months
          </div>
          <div className="flex flex-row gap-3 items-center">
            <BatteryWarrantyMonthsInput
              id={listing.id}
              value={listing.remaining_battery_warranty_months ?? 0}
              className="px-2"
            />
          </div>
        </div>
        <div className="h-px bg-gray-200 w-full" />

        <div className="flex flex-col">
          <div className="text-xs text-gray-500 px-2 py-1">Autopilot</div>
          <div className="flex flex-row gap-3 items-center">
            <AutopilotSelector
              id={listing.vehicle?.id ?? ''}
              value={listing.vehicle?.autopilot_features || null}
              className="px-2"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <div className="text-xs text-gray-500 px-2 py-1">Previous Owners</div>
          <div className="flex flex-row gap-3 items-center">
            <VehiclePreviousOwnersSelector
              id={listing.id}
              value={
                listing.previous_owners === 0 ? 1 : listing.previous_owners
              }
              className="px-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default NonMobileAside;
